import { somDeMoeda } from '../../design/Comemoracoes';
import { marcosDaSemana } from './marcos';
import { soltarConfete } from '../../design/confete';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { Eu } from '../../acesso';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import { lerReais, paraCampo, reais } from '../../produtos';
import { ganhoDoCartao } from './ganho-do-cartao';
import { mostraValor, useValoresOcultos } from '../../design/sigilo';
import { TopoMovel } from '../../shell/ShellBalconista';
import {
  ROTULO_DO_PROGRAMA,
  SIGLA_DO_PROGRAMA,
  buscarPorSintoma,
  buscarProduto,
  cancelarVenda,
  dataCurta,
  encontrarProduto,
  enviarFoto,
  hora,
  novaChave,
  opcoesDoProduto,
  previa as pedirPrevia,
  registrarVenda,
  resumoDoBalconista,
  unidades,
  verificarVenda,
} from '../../vendas';
import type {
  Faixa,
  MinhaVenda,
  OpcoesDeVenda,
  PedidoDeVenda,
  Previa,
  ProgramaDeVenda,
  ResultadoDaBusca,
  ResumoDoBalconista,
  VendaRegistrada,
} from '../../vendas';
import { agoraCarimbo, carimbarFoto, type FonteDaFoto } from './foto';
import { CameraDaFoto } from './CameraDaFoto';
import { Leitor, leitorDisponivel } from './Leitor';
import { bloqueioDaVenda, metaDaSemana } from './meta-e-bloqueio';
import './registrar.css';

/**
 * TELA 08 — REGISTRAR VENDA. "Bipar, escolher o preço, registrar. Três toques."
 *
 * Os campos são os do InovaPay de hoje (tipo, código, faixa MÁX/MÍN/outro valor, quantidade,
 * comprovação do pré-vencido) — nenhum inventado, nenhum removido. O que muda é o custo de cada
 * passo: o cartão já mostra quanto a pessoa ganha em cada opção, o botão fica na zona do polegar
 * e a confirmação não interrompe o próximo lançamento (30 s para desfazer).
 *
 * 🚨 Quanto ganha vem SEMPRE do servidor (`/api/venda/previa`, o mesmo motor do registro). A tela
 * só mostra. O botão trava no primeiro toque; um reenvio usa a MESMA chave e não duplica.
 */

type Escolha = 'a' | 'b' | 'base' | 'pmc' | 'outro';
type Envio =
  | { readonly fase: 'parado' }
  | { readonly fase: 'enviando' }
  | { readonly fase: 'incerto' }
  | { readonly fase: 'recusado'; readonly mensagem: string };
type Foto = { readonly id: string; readonly url: string };

const TIPOS: readonly { tipo: ProgramaDeVenda; rotulo: string; icone: NomeDoIcone }[] = [
  { tipo: 'vitamina', rotulo: 'Vitamina', icone: 'pill' },
  { tipo: 'pre_vencido', rotulo: 'Pré-vencido', icone: 'clock' },
  { tipo: 'paradao', rotulo: 'Paradão', icone: 'cycle' },
];

function usePC(): boolean {
  const consulta = '(min-width: 1024px)';
  const [pc, setPC] = useState(() => window.matchMedia(consulta).matches);
  useEffect(() => {
    const m = window.matchMedia(consulta);
    const aoMudar = () => setPC(m.matches);
    m.addEventListener('change', aoMudar);
    return () => m.removeEventListener('change', aoMudar);
  }, []);
  return pc;
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

function programasDoProduto(p: OpcoesDeVenda): ProgramaDeVenda[] {
  return [
    ...(p.vitamina ? (['vitamina'] as const) : []),
    ...(p.preVencido ? (['pre_vencido'] as const) : []),
    ...(p.paradao ? (['paradao'] as const) : []),
  ];
}

/** Os cards da vitamina como o servidor os entende: 2 faixas → MÁX = 'a' (ordem 2), MÍN = 'b'. */
function cardsDaVitamina(p: OpcoesDeVenda) {
  const f = [...(p.vitamina?.faixas ?? [])].sort((x, y) => x.ordem - y.ordem);
  if (f.length === 1 && f[0]) return [{ chave: 'a' as const, rotulo: 'PREÇO', ...f[0] }];
  const [min, max] = f;
  return [
    ...(max ? [{ chave: 'a' as const, rotulo: 'MÁX', ...max }] : []),
    ...(min ? [{ chave: 'b' as const, rotulo: 'MÍN', ...min }] : []),
  ];
}

export function RegistrarVenda({
  eu,
  sessao,
  parametros,
}: {
  eu: Eu | null;
  sessao: Sessao;
  parametros: URLSearchParams;
}) {
  const ocultos = useValoresOcultos();
  const pc = usePC();
  const online = useOnline();
  const [resumo, setResumo] = useState<ResumoDoBalconista | null>(null);
  const [tipo, setTipo] = useState<ProgramaDeVenda>('vitamina');
  const [codigo, setCodigo] = useState('');
  const [produto, setProduto] = useState<OpcoesDeVenda | null>(null);
  const [procurando, setProcurando] = useState(false);
  const [erroDoCodigo, setErroDoCodigo] = useState<string | null>(null);
  const [escolha, setEscolha] = useState<Escolha | null>(null);
  const [outro, setOutro] = useState('');
  const [qtd, setQtd] = useState(1);
  const [obs, setObs] = useState('');
  const [obsAberta, setObsAberta] = useState(false);
  const [validade, setValidade] = useState('');
  const [lote, setLote] = useState('');
  const [foto, setFoto] = useState<Foto | null>(null);
  const [fotoEnviando, setFotoEnviando] = useState(false);
  const [erroDaFoto, setErroDaFoto] = useState<string | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [premioNoPmc, setPremioNoPmc] = useState<string | null>(null);
  const [envio, setEnvio] = useState<Envio>({ fase: 'parado' });
  const [registrada, setRegistrada] = useState<VendaRegistrada | null>(null);
  const [desfazer, setDesfazer] = useState<{ venda: VendaRegistrada; ate: number } | null>(null);
  const [agora, setAgora] = useState(() => Date.now());
  const [aviso, setAviso] = useState<string | null>(null);
  const [busca, setBusca] = useState<string | null>(parametros.get('buscar') ? '' : null);
  const [leitor, setLeitor] = useState(false);
  // o aparelho lê código pela câmera? (iPhone/Safari não tem BarcodeDetector)
  const [comCamera] = useState(leitorDisponivel);
  const pedidoRef = useRef<PedidoDeVenda | null>(null);
  const campoCodigo = useRef<HTMLInputElement>(null);
  const [camera, setCamera] = useState(false);
  // A prévia da foto é um arquivo na memória: solta quando troca ou quando a tela sai.
  useEffect(
    () => () => {
      if (foto) URL.revokeObjectURL(foto.url);
    },
    [foto],
  );

  const recarregarResumo = useCallback(() => {
    void resumoDoBalconista().then((r) => {
      if (r.ok) setResumo(r.dados);
    });
  }, []);
  useEffect(recarregarResumo, [recarregarResumo]);
  // 50/75/90% da meta da semana e a meta batida (tela cheia): comemora na hora que passa.
  useEffect(() => {
    if (resumo && eu) marcosDaSemana(eu.login, resumo);
  }, [resumo, eu]);

  // O primeiro tipo liberado vira o padrão (o paradão vem escondido, como no 1.0).
  useEffect(() => {
    if (!resumo) return;
    if (!resumo.programas[tipo]) {
      const primeiro = TIPOS.find((t) => resumo.programas[t.tipo]);
      if (primeiro) setTipo(primeiro.tipo);
    }
  }, [resumo, tipo]);

  const limparVenda = useCallback(() => {
    setEscolha(null);
    setOutro('');
    setQtd(1);
    setObs('');
    setObsAberta(false);
    setValidade('');
    setLote('');
    setFoto(null);
    setErroDaFoto(null);
    setPrevia(null);
    setPremioNoPmc(null);
    setEnvio({ fase: 'parado' });
    pedidoRef.current = null;
  }, []);

  const abrirProduto = useCallback(
    (p: OpcoesDeVenda, preferido?: ProgramaDeVenda) => {
      limparVenda();
      setProduto(p);
      setErroDoCodigo(null);
      setCodigo(p.codigo ?? '');
      const disponiveis = programasDoProduto(p);
      const alvo = preferido ?? tipo;
      if (disponiveis.includes(alvo)) setTipo(alvo);
      else if (disponiveis.length === 1 && disponiveis[0]) setTipo(disponiveis[0]);
    },
    [limparVenda, tipo],
  );

  const procurarCodigo = useCallback(
    async (texto: string) => {
      const t = texto.trim();
      if (!t) return;
      // Não é código (tem letra): vira busca pelo nome.
      if (/[a-zA-ZÀ-ú]/.test(t) && !/^\d+$/.test(t)) {
        setBusca(t);
        return;
      }
      setProcurando(true);
      setErroDoCodigo(null);
      const r = await encontrarProduto(t);
      setProcurando(false);
      if (r.ok) abrirProduto(r.dados);
      else if (r.falha.tipo === 'offline') setErroDoCodigo('Sem conexão para procurar o produto.');
      else if (r.falha.tipo === 'invalido')
        setErroDoCodigo(
          r.falha.mensagem ||
            'Produto sem cadastro. Peça o cadastro a um administrador antes de vender.',
        );
      else setErroDoCodigo('Não foi possível procurar agora. Tente de novo.');
    },
    [abrirProduto],
  );

  const abrirPorId = useCallback(
    async (id: string, preferido?: ProgramaDeVenda) => {
      setProcurando(true);
      const r = await opcoesDoProduto(id);
      setProcurando(false);
      if (r.ok) abrirProduto(r.dados, preferido);
      else setErroDoCodigo('Não foi possível abrir o produto. Tente de novo.');
      return r.ok ? r.dados : null;
    },
    [abrirProduto],
  );

  // Veio da Home ("Seus mais vendidos"): já abre o produto.
  const produtoDaRota = parametros.get('produto');
  useEffect(() => {
    if (produtoDaRota) void abrirPorId(produtoDaRota);
    // Só quando a rota muda: `abrirPorId` muda a cada tipo escolhido e reabriria o produto.
  }, [produtoDaRota]);

  const disponiveis = produto ? programasDoProduto(produto) : [];
  const ambiguo = produto !== null && !disponiveis.includes(tipo) && disponiveis.length > 1;
  const foraDeTodos = produto !== null && disponiveis.length === 0;
  const programa: ProgramaDeVenda | null = produto && disponiveis.includes(tipo) ? tipo : null;
  const opcoesPv =
    programa === 'pre_vencido'
      ? produto?.preVencido
      : programa === 'paradao'
        ? produto?.paradao
        : null;

  // Faixa + preço da escolha, do jeito que o servidor espera.
  const escolhido = useMemo((): { faixa: Faixa; preco: string | null } | null => {
    if (!produto || !programa || !escolha) return null;
    if (programa === 'vitamina') {
      if (escolha === 'a' || escolha === 'b') return { faixa: escolha, preco: null };
      if (escolha === 'outro') {
        const v = lerReais(outro);
        return v ? { faixa: 'c', preco: v } : null;
      }
      return null;
    }
    if (escolha === 'base')
      return opcoesPv?.venderPor ? { faixa: 'pv', preco: opcoesPv.venderPor } : null;
    if (escolha === 'pmc') return produto.pmc ? { faixa: 'pv', preco: produto.pmc } : null;
    if (escolha === 'outro') {
      const v = lerReais(outro);
      return v ? { faixa: 'pv', preco: v } : null;
    }
    return null;
  }, [produto, programa, escolha, outro, opcoesPv]);

  // Prévia do servidor: muda com o preço e a quantidade.
  const qtdValida = Number.isInteger(qtd) && qtd >= 1 && qtd <= 9999;
  useEffect(() => {
    setPrevia(null);
    if (!produto || !programa || !escolhido || !qtdValida) return;
    let vivo = true;
    const t = window.setTimeout(() => {
      void pedirPrevia({
        productId: produto.productId,
        programa,
        faixa: escolhido.faixa,
        preco: escolhido.preco,
        quantidade: qtd,
      }).then((r) => {
        if (vivo && r.ok) setPrevia(r.dados);
      });
    }, 180);
    return () => {
      vivo = false;
      window.clearTimeout(t);
    };
  }, [produto, programa, escolhido, qtd, qtdValida]);

  // Quanto o PMC rende no pré-vencido/paradão (o card mostra antes de a pessoa escolher).
  useEffect(() => {
    setPremioNoPmc(null);
    if (!produto?.pmc || (programa !== 'pre_vencido' && programa !== 'paradao')) return;
    let vivo = true;
    void pedirPrevia({
      productId: produto.productId,
      programa,
      faixa: 'pv',
      preco: produto.pmc,
      quantidade: 1,
    }).then((r) => {
      if (vivo && r.ok && !r.dados.bloqueio) setPremioNoPmc(r.dados.premioUnitario);
    });
    return () => {
      vivo = false;
    };
  }, [produto, programa]);

  // Relógio do "Desfazer".
  useEffect(() => {
    if (!desfazer) return;
    const t = window.setInterval(() => {
      setAgora(Date.now());
      if (Date.now() >= desfazer.ate) setDesfazer(null);
    }, 250);
    return () => window.clearInterval(t);
  }, [desfazer]);

  /* ---------------- o que falta ---------------- */

  const loteDoEstoque = produto?.preVencido?.lotes.find(
    (l) =>
      (l.codigo ?? '').toUpperCase() ===
      lote
        .trim()
        .toUpperCase()
        .replace(/^\(L\)\s*/, ''),
  );
  const estoqueDisponivel =
    programa === 'pre_vencido'
      ? (loteDoEstoque?.quantidade ?? null)
      : programa === 'paradao'
        ? (produto?.estoque ?? null)
        : null;
  const semEstoque = estoqueDisponivel !== null && Number(estoqueDisponivel) < qtd;
  const validadeAte = produto?.preVencido?.validadeAte ?? null;
  const validadeLonge = validade !== '' && validadeAte !== null && validade > validadeAte;

  const provas = {
    validade: validade !== '' && !validadeLonge,
    lote: lote.trim() !== '',
    foto: foto !== null,
  };
  const provasFeitas = Number(provas.validade) + Number(provas.lote) + Number(provas.foto);

  const pendencia: string | null = !produto
    ? 'Escolha o produto para continuar'
    : ambiguo
      ? 'Escolha a categoria para continuar'
      : foraDeTodos || !programa
        ? 'Este produto não pode ser registrado nesta categoria'
        : !escolha
          ? 'Escolha o preço para continuar'
          : !escolhido
            ? 'Digite o valor vendido'
            : !qtdValida
              ? 'Quantidade inválida'
              : previa?.bloqueio
                ? previa.bloqueio
                : semEstoque
                  ? 'Ajuste a quantidade ao estoque'
                  : programa === 'pre_vencido' && !provas.validade
                    ? 'Falta a validade para registrar'
                    : programa === 'pre_vencido' && !provas.lote
                      ? 'Falta o nº do lote para registrar'
                      : programa === 'pre_vencido' && !provas.foto
                        ? 'Falta a foto para registrar'
                        : programa === 'paradao' && !foto && obs.trim().length < 10
                          ? 'Falta a comprovação (foto ou observação)'
                          : previa === null
                            ? 'Calculando…'
                            : null;
  const travado = envio.fase === 'enviando' || envio.fase === 'incerto';
  // U6: bloqueio do Giro ou manutenção trava o formulário no TOPO, antes de preencher tudo
  const bloqueio = bloqueioDaVenda(resumo);
  const podeRegistrar = pendencia === null && online && !travado && bloqueio === null;

  /* ---------------- ações ---------------- */

  const enviar = async (pedido: PedidoDeVenda, verificando = false) => {
    pedidoRef.current = pedido;
    setEnvio({ fase: 'enviando' });
    // "Verificar" pergunta pela chave antes de reenviar (item 31): nunca "recusada" sem certeza
    const d = verificando
      ? await verificarVenda(sessao, pedido)
      : await registrarVenda(sessao, pedido);
    if (d.tipo === 'confirmado') {
      pedidoRef.current = null;
      soltarConfete(90);
      somDeMoeda();
      setRegistrada(d.venda);
      setDesfazer({ venda: d.venda, ate: Date.now() + 30_000 });
      setAgora(Date.now());
      setEnvio({ fase: 'parado' });
      setAviso(null);
      recarregarResumo();
      if (pc) {
        // No PC o formulário volta limpo na hora: o resumo do dia fica ao lado.
        setProduto(null);
        setCodigo('');
        limparVenda();
        campoCodigo.current?.focus();
      }
    } else if (d.tipo === 'incerto') setEnvio({ fase: 'incerto' });
    else {
      pedidoRef.current = null;
      setEnvio({ fase: 'recusado', mensagem: d.mensagem });
    }
  };

  const registrar = () => {
    if (!podeRegistrar || !produto || !programa || !escolhido) return;
    void enviar({
      chaveIdempotencia: novaChave(),
      productId: produto.productId,
      programa,
      quantidade: qtd,
      faixa: escolhido.faixa,
      precoUnitario: escolhido.preco,
      loteCodigo: programa === 'pre_vencido' ? lote.trim() : null,
      validade: programa === 'pre_vencido' ? validade : null,
      evidenciaId: foto?.id ?? null,
      observacao: obs.trim() || null,
    });
  };

  /** "Verificar": pergunta pela MESMA tentativa. Se já gravou, volta a mesma venda. */
  const verificar = () => {
    if (pedidoRef.current) void enviar(pedidoRef.current, true);
  };

  const desfazerAgora = async () => {
    if (!desfazer) return;
    const alvo = desfazer.venda;
    setDesfazer(null);
    const r = await cancelarVenda(sessao, alvo.id);
    if (r.ok) {
      setAviso(`Venda desfeita: ${alvo.descricao}. Nada foi contado.`);
      setRegistrada(null);
      recarregarResumo();
    } else setAviso(r.mensagem);
  };

  const novaVenda = () => {
    setRegistrada(null);
    setProduto(null);
    setCodigo('');
    limparVenda();
    window.setTimeout(() => campoCodigo.current?.focus(), 0);
  };

  /** Repetir: mesmo produto, categoria, preço e quantidade. A comprovação é sempre nova. */
  const repetir = async (v: MinhaVenda | VendaRegistrada, faixaGravada?: string) => {
    setRegistrada(null);
    const p = await abrirPorId(v.productId, v.programa);
    if (!p) return;
    setTipo(v.programa);
    setQtd(v.quantidade);
    const f = faixaGravada ?? ('faixa' in v ? v.faixa : null);
    if (v.programa === 'vitamina') {
      if (f === 'a' || f === 'b') setEscolha(f);
      else {
        setEscolha('outro');
        setOutro(paraCampo(v.precoUnitario));
      }
    } else {
      const op = v.programa === 'pre_vencido' ? p.preVencido : p.paradao;
      if (op?.venderPor === v.precoUnitario) setEscolha('base');
      else if (p.pmc === v.precoUnitario) setEscolha('pmc');
      else {
        setEscolha('outro');
        setOutro(paraCampo(v.precoUnitario));
      }
      if ('loteCodigo' in v && v.loteCodigo) setLote(v.loteCodigo);
      if ('validade' in v && v.validade) setValidade(v.validade);
    }
  };

  const tirarFoto = async (arquivo: FonteDaFoto | undefined) => {
    if (!arquivo || !produto) return;
    setErroDaFoto(null);
    setFotoEnviando(true);
    try {
      const nome = eu ? eu.nomeExibicao || eu.nome : 'Balconista';
      const carimbada = await carimbarFoto(arquivo, [
        agoraCarimbo(),
        `${nome} · cód ${eu?.login ?? '—'}`,
        `${produto.descricao}${lote.trim() ? ` · lote ${lote.trim().toUpperCase()}` : ''}`,
      ]);
      const r = await enviarFoto(sessao, carimbada);
      if (r.ok) setFoto({ id: r.id, url: URL.createObjectURL(carimbada) });
      else setErroDaFoto(r.mensagem);
    } catch {
      setErroDaFoto('Não foi possível ler a foto. Tire de novo.');
    } finally {
      setFotoEnviando(false);
    }
  };

  const aoEnviarCodigo = (e: FormEvent) => {
    e.preventDefault();
    void procurarCodigo(codigo);
  };

  /* ---------------- pedaços ---------------- */

  const r = resumo;
  const tiposVisiveis = TIPOS.filter((t) => r?.programas[t.tipo] ?? t.tipo !== 'paradao');
  const vendasHoje = r?.ultimas ?? [];
  const segundos = desfazer ? Math.max(0, Math.ceil((desfazer.ate - agora) / 1000)) : 0;

  const seletorDeTipo = (
    <div className="rv-bloco rv-bloco-tipo">
      <div className="rv-rotulo rv-so-movel">Tipo do produto</div>
      <div className="rv-tipos" role="radiogroup" aria-label="Tipo do produto">
        {tiposVisiveis.map((t) => (
          <button
            key={t.tipo}
            type="button"
            role="radio"
            aria-checked={tipo === t.tipo}
            className={`rv-tipo${tipo === t.tipo ? ' rv-sel' : ''}`}
            disabled={travado}
            onClick={() => {
              setTipo(t.tipo);
              setEscolha(null);
              setOutro('');
            }}
          >
            <Icone nome={t.icone} tamanho={18} traco={1.6} />
            <span>{t.rotulo}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const campoDeCodigo = (
    <div className="rv-bloco rv-bloco-codigo">
      <div className="rv-rotulo rv-rotulo-codigo">Código do produto</div>
      <form className="rv-codigo" onSubmit={aoEnviarCodigo}>
        <input
          ref={campoCodigo}
          className={`rv-codigo-campo${erroDoCodigo ? ' rv-campo-erro' : ''}`}
          value={codigo}
          onChange={(e) => {
            setCodigo(e.target.value);
            setErroDoCodigo(null);
          }}
          placeholder="Bipa, digita ou busca"
          inputMode="search"
          autoComplete="off"
          enterKeyHint="search"
          aria-label="Código do produto"
          disabled={travado}
        />
        {/* U10: sem leitor pela câmera (iPhone/Safari) o botão nem aparece — digita ou bipa */}
        {comCamera && (
          <button
            type="button"
            className="rv-codigo-cam"
            onClick={() => setLeitor(true)}
            aria-label="Ler código pela câmera"
            disabled={travado}
          >
            <Icone nome="cam" tamanho={19} traco={1.5} />
          </button>
        )}
      </form>
      {procurando && <div className="rv-dica">Procurando…</div>}
      {erroDoCodigo && (
        <div className="rv-erro-bloco" role="alert">
          <Icone nome="warn" tamanho={16} traco={1.6} />
          <span>{erroDoCodigo}</span>
        </div>
      )}
      {!produto && (
        <button type="button" className="rv-buscar-nome" onClick={() => setBusca('')}>
          <Icone nome="search" tamanho={16} traco={1.6} />
          <span>Não sabe o código? Busque pelo nome</span>
        </button>
      )}
    </div>
  );

  const cartaoDoProduto = produto && (
    <div className="rv-produto">
      <div className="rv-produto-foto" aria-hidden="true">
        <Icone nome="image" tamanho={18} traco={1.4} />
      </div>
      <div className="rv-produto-texto">
        <div className="rv-produto-nome">{produto.descricao}</div>
        <div className="rv-produto-meta">
          {[produto.codigo, produto.fabricante].filter(Boolean).join(' · ')}
          <span className="rv-so-pc-tab">
            {produto.estoque !== null ? ` · ${unidades(produto.estoque)} em estoque` : ''}
          </span>
        </div>
        <div className="rv-produto-chips rv-nao-pc-tab">
          {programa && <span className="rv-chip-sigla">{SIGLA_DO_PROGRAMA[programa]}</span>}
          {produto.estoque !== null && (
            <span
              className={`rv-chip ${Number(produto.estoque) <= 3 ? 'rv-chip-pouco' : 'rv-chip-ok'}`}
            >
              {Number(produto.estoque) <= 3 ? 'só ' : ''}
              {unidades(produto.estoque)} em estoque
            </span>
          )}
          {programa !== 'vitamina' && produto.pmc && (
            <span className="rv-chip rv-chip-pmc">PMC {reais(produto.pmc)}</span>
          )}
        </div>
      </div>
      <button type="button" className="rv-trocar" onClick={novaVenda} disabled={travado}>
        <span className="rv-so-pc">Trocar produto</span>
        <span className="rv-nao-pc">Trocar</span>
      </button>
    </div>
  );

  const escolhaDeCategoria = produto && (ambiguo || foraDeTodos) && (
    <div className="rv-bloco">
      {foraDeTodos ? (
        <div className="rv-alerta">
          <Icone nome="warn" tamanho={16} traco={1.6} />
          <div>
            <strong>Este produto não está em nenhuma categoria de premiação.</strong>
            <p>Vitamina, pré-vencido e paradão são configurados pelo administrador.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="rv-alerta">
            <Icone nome="warn" tamanho={16} traco={1.6} />
            <div>
              <strong>
                Este produto está em {disponiveis.length === 2 ? 'duas' : 'três'} categorias
              </strong>
              <p>Escolha como quer registrar. A premiação muda conforme a escolha.</p>
            </div>
          </div>
          {disponiveis.map((d) => (
            <button key={d} type="button" className="rv-categoria" onClick={() => setTipo(d)}>
              <Icone
                nome={d === 'vitamina' ? 'pill' : d === 'pre_vencido' ? 'clock' : 'cycle'}
                tamanho={18}
              />
              <span className="rv-categoria-texto">
                <span className="rv-categoria-nome">Vender como {rotuloGrande(d)}</span>
                <span className="rv-categoria-sub">{resumoDaCategoria(produto, d, ocultos)}</span>
              </span>
              <Icone nome="chev-r" tamanho={16} />
            </button>
          ))}
          <p className="rv-nota">
            Enquanto não escolher, o botão de registrar continua indisponível.
          </p>
        </>
      )}
    </div>
  );

  const cartao = (
    chave: Escolha,
    rotulo: string,
    preco: string,
    doServidor: string | null,
    compacto: boolean,
  ) => {
    const sel = escolha === chave;
    // o MESMO número da prévia (servidor), nunca uma conta da tela
    const ganha = ganhoDoCartao(chave, escolha, previa, doServidor);
    return (
      <button
        key={chave}
        type="button"
        role="radio"
        aria-checked={sel}
        className={`rv-preco${sel ? ' rv-sel' : ''}${compacto ? ' rv-preco-compacto' : ''}`}
        onClick={() => {
          // Comemora quando escolhe um card que paga (dono, 25/09; o 1.0 também soltava).
          if (!sel && ganha !== null && Number(ganha) > 0) {
            soltarConfete();
            somDeMoeda();
          }
          setEscolha(chave);
        }}
        disabled={travado}
      >
        <span className="rv-preco-topo">
          <span className="rv-preco-rotulo">{rotulo}</span>
          <span className={`rv-marca${sel ? ' rv-marca-sel' : ''}`} aria-hidden="true">
            {sel && <Icone nome="check" tamanho={11} traco={2.4} />}
          </span>
        </span>
        {compacto ? (
          <span className="rv-preco-linha">
            <span className="rv-preco-valor">{reais(preco)}</span>
            {mostraValor(ocultos, ganha) && (
              <span className="rv-preco-ganha-curto">+{reais(ganha)}</span>
            )}
          </span>
        ) : (
          <>
            <span className="rv-preco-valor">{reais(preco)}</span>
            {!ocultos && (
              <span className="rv-preco-ganha">
                <span className="rv-preco-ganha-rotulo">
                  <span className="rv-nao-pc-tab">você ganha</span>
                  <span className="rv-so-pc-tab">ganha</span>
                </span>
                <span className="rv-preco-ganha-valor">{ganha !== null ? reais(ganha) : '—'}</span>
              </span>
            )}
          </>
        )}
      </button>
    );
  };

  const outroValor = (
    <div
      className={`rv-outro${escolha === 'outro' ? ' rv-sel' : ''}${previa?.bloqueio && escolha === 'outro' ? ' rv-outro-erro' : ''}`}
    >
      <span className="rv-outro-rotulo">
        <span className="rv-nao-pc-tab">Outro valor</span>
        <span className="rv-so-pc-tab">OUTRO VALOR</span>
      </span>
      <label className="rv-outro-campo">
        <span>R$</span>
        <input
          value={outro}
          inputMode="decimal"
          placeholder="0,00"
          aria-label="Outro valor vendido"
          disabled={travado}
          onFocus={() => setEscolha('outro')}
          onChange={(e) => {
            setEscolha('outro');
            setOutro(e.target.value.replace(/[^\d,.]/g, ''));
          }}
        />
      </label>
      {/* Motor (packages/domain premiacao/motor.ts): entre dois cards o prêmio é PROPORCIONAL. */}
      <span className="rv-outro-nota rv-so-pc-tab">
        {programa === 'vitamina'
          ? 'entre dois cards, prêmio proporcional'
          : 'o prêmio segue o preço'}
      </span>
    </div>
  );

  const precos = produto && programa && (
    <div className="rv-bloco">
      <div className="rv-rotulo rv-rotulo-preco">Por quanto você vendeu?</div>
      {programa === 'vitamina' ? (
        <>
          <div className="rv-precos" role="radiogroup" aria-label="Preço">
            {cardsDaVitamina(produto).map((c) =>
              cartao(c.chave, c.rotulo, c.preco, c.premio, false),
            )}
            {outroValor}
          </div>
          {ocultos ? (
            cardsDaVitamina(produto).at(-1) && (
              <p className="rv-nota rv-nao-pc-tab">
                Abaixo de {reais(cardsDaVitamina(produto).at(-1)?.preco ?? null)} não premia.
              </p>
            )
          ) : (
            <p className="rv-nota rv-nao-pc-tab">
              Prêmio no card:{' '}
              {cardsDaVitamina(produto)
                .map((c) => `${reais(c.preco)} → ${reais(c.premio)}`)
                .join(' · ')}
              {cardsDaVitamina(produto).length > 1
                ? ' · entre dois cards, proporcional ao preço'
                : ''}
              {cardsDaVitamina(produto).at(-1)
                ? ` · abaixo de ${reais(cardsDaVitamina(produto).at(-1)?.preco ?? null)} não premia.`
                : ''}
            </p>
          )}
        </>
      ) : (
        <>
          <div className="rv-precos rv-precos-pv" role="radiogroup" aria-label="Preço">
            {opcoesPv?.venderPor
              ? cartao('base', 'VENDER POR', opcoesPv.venderPor, opcoesPv.premioNaBase, true)
              : null}
            {produto.pmc ? cartao('pmc', 'PMC (MÁX)', produto.pmc, premioNoPmc, true) : null}
            {outroValor}
          </div>
          <p className="rv-nota">
            {opcoesPv?.venderPor
              ? 'O prêmio parte da base e sobe com o preço, até o teto. PMC é o máximo.'
              : 'Sem preço base cadastrado: esta venda paga R$ 0,00 (regra do dono). PMC é o máximo.'}
          </p>
        </>
      )}
      {previa?.bloqueio && (
        <div className="rv-erro-campo" role="alert">
          <Icone nome="warn" tamanho={14} traco={1.7} />
          {previa.bloqueio}
        </div>
      )}
    </div>
  );

  const comprovacaoPv = produto && programa === 'pre_vencido' && (
    <div className="rv-prova">
      <div className="rv-prova-topo">
        <Icone nome="warn" tamanho={16} traco={1.6} />
        <span>Comprovação obrigatória</span>
        <span className="rv-prova-conta">{provasFeitas} de 3</span>
      </div>
      <label className="rv-prova-linha">
        <Passo feito={provas.validade} n={1} erro={validadeLonge} />
        <span className="rv-prova-texto">
          <span className="rv-prova-nome">Validade na caixa</span>
          <span className="rv-prova-sub">
            {validadeLonge
              ? `Muito longe: aceita até ${dataCurta(validadeAte ?? '')}`
              : `Aceita até ${validadeAte ? dataCurta(validadeAte) : '—'}`}
          </span>
        </span>
        <input
          type="date"
          className="rv-prova-campo"
          value={validade}
          max={validadeAte ?? undefined}
          onChange={(e) => setValidade(e.target.value)}
          disabled={travado}
          aria-label="Validade na caixa"
        />
      </label>
      <label className="rv-prova-linha">
        <Passo feito={provas.lote} n={2} />
        <span className="rv-prova-texto">
          <span className="rv-prova-nome">Nº do lote</span>
          <span className="rv-prova-sub">O "L" impresso na caixa</span>
        </span>
        <input
          className="rv-prova-campo rv-prova-lote"
          value={lote}
          placeholder="L1234"
          autoCapitalize="characters"
          autoComplete="off"
          onChange={(e) => setLote(e.target.value.toUpperCase())}
          disabled={travado}
          aria-label="Número do lote"
        />
      </label>
      {produto.preVencido && produto.preVencido.lotes.length > 0 && !provas.lote && (
        <div className="rv-prova-lotes">
          Lotes em pré-vencido:{' '}
          {produto.preVencido.lotes.map((l, i) => (
            <button
              key={`${l.codigo ?? ''}-${String(i)}`}
              type="button"
              onClick={() => {
                setLote(l.codigo ?? '');
                if (l.validade) setValidade(l.validade);
              }}
            >
              {l.codigo ?? 'sem código'}
              {l.validade ? ` · ${dataCurta(l.validade)}` : ''}
            </button>
          ))}
        </div>
      )}
      <div className="rv-prova-foto">
        <div className="rv-prova-linha rv-prova-linha-sem-borda">
          <Passo feito={provas.foto} n={3} />
          <span className="rv-prova-texto">
            <span className="rv-prova-nome">Foto do lote na caixa</span>
            <span className="rv-prova-sub">Tirada agora, com data e seu nome</span>
          </span>
          {foto && <img className="rv-prova-miniatura" src={foto.url} alt="Foto do lote" />}
        </div>
        {botaoDaFoto(foto ? 'Tirar outra foto' : 'Abrir câmera')}
      </div>
    </div>
  );

  function botaoDaFoto(texto: string) {
    return (
      <>
        <button
          type="button"
          className={`rv-camera${foto ? ' rv-camera-secundaria' : ''}`}
          onClick={() => setCamera(true)}
          disabled={travado || fotoEnviando}
        >
          <Icone nome="cam" tamanho={18} traco={1.5} />
          {fotoEnviando ? 'Enviando a foto…' : texto}
        </button>
        {erroDaFoto && (
          <div className="rv-erro-campo" role="alert">
            <Icone nome="warn" tamanho={14} traco={1.7} />
            {erroDaFoto}
          </div>
        )}
      </>
    );
  }

  const comprovacaoParadao = produto && programa === 'paradao' && (
    <div className="rv-prova">
      <div className="rv-prova-topo">
        <Icone nome="warn" tamanho={16} traco={1.6} />
        <span>Comprovação do paradão</span>
        <span className="rv-prova-conta">
          {foto || obs.trim().length >= 10 ? 'ok' : 'foto ou texto'}
        </span>
      </div>
      <div className="rv-prova-foto">
        <p className="rv-prova-sub rv-prova-explica">
          Tire uma foto do produto <strong>ou</strong> escreva na observação quem comprou (mínimo 10
          letras).
        </p>
        {foto && <img className="rv-prova-miniatura" src={foto.url} alt="Foto do produto" />}
        {botaoDaFoto(foto ? 'Tirar outra foto' : 'Abrir câmera')}
      </div>
    </div>
  );

  const estoqueCurto = semEstoque && (
    <div className="rv-erro-campo" role="alert">
      <Icone nome="warn" tamanho={14} traco={1.7} />
      Tem {unidades(estoqueDisponivel)} em estoque. Ajuste a quantidade para continuar.
    </div>
  );

  const quantidade = (
    <div className={`rv-qtd${semEstoque ? ' rv-qtd-erro' : ''}`}>
      <span className="rv-qtd-rotulo">
        <span className="rv-so-pc">Qtd</span>
        <span className="rv-nao-pc">Quantidade</span>
      </span>
      <div className="rv-qtd-passo">
        <button
          type="button"
          onClick={() => setQtd((q) => Math.max(1, q - 1))}
          disabled={travado || qtd <= 1}
          aria-label="Diminuir"
        >
          <Icone nome="minus" tamanho={18} traco={1.7} />
        </button>
        <input
          value={Number.isNaN(qtd) ? '' : String(qtd)}
          inputMode="numeric"
          aria-label="Quantidade"
          disabled={travado}
          onChange={(e) => {
            const d = e.target.value.replace(/\D/g, '').slice(0, 4);
            setQtd(d === '' ? Number.NaN : Number(d));
          }}
          onBlur={() => {
            if (!Number.isInteger(qtd) || qtd < 1) setQtd(1);
          }}
        />
        <button
          type="button"
          onClick={() => setQtd((q) => Math.min(9999, (Number.isInteger(q) ? q : 0) + 1))}
          disabled={travado || qtd >= 9999}
          aria-label="Aumentar"
        >
          <Icone nome="plus" tamanho={18} traco={1.7} />
        </button>
      </div>
    </div>
  );

  const observacao = (
    <div className={`rv-obs${obsAberta || obs ? ' rv-obs-aberta' : ''}`}>
      <button
        type="button"
        className="rv-obs-botao"
        onClick={() => setObsAberta((v) => !v)}
        aria-expanded={obsAberta}
      >
        <span>
          Observação <span className="rv-obs-opcional">(opcional)</span>
        </span>
        <Icone nome="chev-d" tamanho={16} />
      </button>
      <input
        className="rv-obs-campo"
        value={obs}
        maxLength={500}
        placeholder={
          programa === 'paradao' ? 'Ex: vendido para o cliente João' : 'Observação (opcional)'
        }
        onChange={(e) => setObs(e.target.value)}
        disabled={travado}
        aria-label="Observação"
      />
    </div>
  );

  const ganho = previa && !previa.bloqueio && !ocultos && (
    <div className="rv-ganho">
      <div>
        <div className="rv-ganho-rotulo">
          <span className="rv-nao-pc">VOCÊ GANHA NESTA VENDA</span>
          <span className="rv-so-pc">
            VOCÊ GANHA · {qtd} × {reais(previa.premioUnitario)}
          </span>
        </div>
        <div className="rv-ganho-conta rv-nao-pc">
          {qtd} × {reais(previa.premioUnitario)}
        </div>
      </div>
      <div className="rv-ganho-valor">{reais(previa.premioTotal)}</div>
    </div>
  );

  const incerto = envio.fase === 'incerto' && (
    <div className="rv-incerto" role="alert">
      <div className="rv-incerto-caixa">
        <Icone nome="clock" tamanho={17} traco={1.6} />
        <div>
          <strong>Não conseguimos confirmar o registro</strong>
          <p>A venda pode ter sido salva. Estamos verificando — não registre de novo ainda.</p>
        </div>
      </div>
      <div className="rv-incerto-acoes">
        <a className="rv-botao-claro" href="#/minhas">
          Ver minhas vendas
        </a>
        <button type="button" className="rv-botao-vermelho" onClick={verificar}>
          <Icone nome="retry" tamanho={16} traco={1.7} />
          Verificar
        </button>
      </div>
    </div>
  );

  const recusado = envio.fase === 'recusado' && (
    <div className="rv-recusado" role="alert">
      <Icone nome="warn" tamanho={16} traco={1.6} />
      <span>{envio.mensagem}</span>
    </div>
  );

  const botaoRegistrar = (
    <button
      type="button"
      className="rv-registrar"
      disabled={!podeRegistrar}
      aria-disabled={!podeRegistrar}
      onClick={registrar}
    >
      {envio.fase === 'enviando' ? (
        <>
          <span className="rv-girando" aria-hidden="true" />
          Registrando…
        </>
      ) : (
        'Registrar venda'
      )}
    </button>
  );

  const rodape = (
    <div className="rv-rodape">
      {!online && (
        <div className="rv-offline">
          <Icone nome="offline" tamanho={16} traco={1.6} />
          Sem conexão. Algumas ações ficam indisponíveis.
        </div>
      )}
      {incerto}
      {recusado}
      {programa && programa !== 'vitamina' ? (
        <div className="rv-rodape-pv">
          {quantidade}
          {(!ocultos || pendencia) && (
            <div className="rv-rodape-resumo">
              <div className="rv-rodape-pendencia">{pendencia ?? 'Você ganha nesta venda'}</div>
              {!ocultos && (
                <div className={`rv-rodape-valor${pendencia ? '' : ' rv-rodape-valor-ok'}`}>
                  {previa && !previa.bloqueio ? reais(previa.premioTotal) : '—'}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        ganho
      )}
      {botaoRegistrar}
      {(!programa || programa === 'vitamina') && pendencia && !previa && online && !travado && (
        <div className="rv-rodape-dica">{pendencia}</div>
      )}
      {!online && <div className="rv-rodape-dica">Conecte-se novamente para registrar.</div>}
    </div>
  );

  const listaDeHoje = (
    <section className="rv-hoje" aria-label="Suas vendas de hoje">
      <div className="rv-hoje-topo">
        <h2>Suas vendas de hoje</h2>
        <a href="#/minhas" className="rv-so-pc-tab">
          Ver todas
        </a>
        {!ocultos && (
          <span className="rv-nao-pc-tab rv-hoje-total">{r ? reais(r.premiacaoHoje) : ''}</span>
        )}
      </div>
      {r !== null && vendasHoje.length === 0 && (
        <div className="rv-hoje-vazio">Nenhuma venda registrada hoje ainda.</div>
      )}
      <div className="rv-hoje-lista">
        {vendasHoje.map((v) => (
          <div
            key={v.id}
            className={`rv-hoje-linha${v.status === 'cancelado' ? ' rv-cancelada' : ''}`}
          >
            <span className="rv-hoje-hora">{hora(v.registradoEm)}</span>
            <div className="rv-hoje-texto">
              <div className="rv-hoje-nome">{v.descricao}</div>
              <div className="rv-hoje-sub">
                {hora(v.registradoEm)} · {v.quantidade} un · {ROTULO_DO_PROGRAMA[v.programa]}
              </div>
            </div>
            <span className="rv-hoje-sigla">{SIGLA_DO_PROGRAMA[v.programa]}</span>
            <span className="rv-hoje-qtd">
              {v.status === 'cancelado' ? 'cancelada' : `${String(v.quantidade)} un`}
            </span>
            {!ocultos && (
              <span className="rv-hoje-valor">
                {v.status === 'cancelado' ? reais('0.00') : reais(v.premioTotal)}
              </span>
            )}
          </div>
        ))}
      </div>
      {r && (
        <div className="rv-hoje-rodape">
          <span>Total do dia</span>
          <strong>
            {r.itensHoje} {r.itensHoje === 1 ? 'item' : 'itens'}
            {mostraValor(ocultos, r.premiacaoHoje) && ` · ${reais(r.premiacaoHoje)}`}
          </strong>
        </div>
      )}
    </section>
  );

  const ultima = r?.ultimaVenda ?? null;
  const atalhos = !produto && (
    <div className="rv-atalhos">
      {r && r.maisVendidos.length > 0 && (
        <section className="rv-mais" aria-label="Seus mais vendidos">
          <div className="rv-mais-topo">
            <h2>Seus mais vendidos</h2>
            <span>toque para preencher</span>
          </div>
          {r.maisVendidos.map((m) => (
            <button
              key={m.productId}
              type="button"
              className="rv-mais-linha"
              onClick={() => void abrirPorId(m.productId)}
            >
              <span className="rv-mais-texto">
                <span className="rv-mais-nome">{m.descricao}</span>
                <span className="rv-mais-meta">
                  {m.codigo ? `${m.codigo} · ` : ''}
                  {m.vezes} {m.vezes === 1 ? 'venda' : 'vendas'}
                </span>
              </span>
              <Icone nome="chev-r" tamanho={16} />
            </button>
          ))}
        </section>
      )}
      {ultima && (
        <button type="button" className="rv-repetir" onClick={() => void repetir(ultima)}>
          <Icone nome="retry" tamanho={17} traco={1.6} />
          <span className="rv-repetir-texto">
            <span className="rv-repetir-nome">Repetir última venda</span>
            <span className="rv-repetir-sub">
              {ultima.descricao} · {ultima.quantidade} un · {tempoDesde(ultima.registradoEm)}
            </span>
          </span>
        </button>
      )}
    </div>
  );

  const toast = desfazer && (
    <div className="rv-toast" role="status">
      <Icone nome="check" tamanho={17} traco={2} />
      <span className="rv-toast-texto">Venda registrada</span>
      <span className="rv-toast-seg">{segundos}s</span>
      <button type="button" onClick={() => void desfazerAgora()}>
        Desfazer
      </button>
    </div>
  );

  const avisoFlutuante = aviso && !desfazer && (
    <div className="rv-toast rv-toast-aviso" role="status">
      <span className="rv-toast-texto">{aviso}</span>
      <button type="button" onClick={() => setAviso(null)} aria-label="Fechar aviso">
        <Icone nome="x" tamanho={14} />
      </button>
    </div>
  );

  const sobrepostos = (
    <>
      {busca !== null && (
        <Busca
          inicial={busca}
          aoFechar={() => setBusca(null)}
          aoEscolher={(item) => {
            setBusca(null);
            void abrirPorId(item.productId, item.programas[0]);
          }}
        />
      )}
      {leitor && (
        <Leitor
          aoFechar={() => setLeitor(false)}
          aoLer={(c) => {
            setLeitor(false);
            setCodigo(c);
            void procurarCodigo(c);
          }}
        />
      )}
      {camera && (
        <CameraDaFoto
          titulo="Foto do produto"
          aoFechar={() => setCamera(false)}
          aoUsar={(f) => {
            setCamera(false);
            void tirarFoto(f);
          }}
        />
      )}
      {toast}
      {avisoFlutuante}
    </>
  );

  const pilula = (
    <span className={`rv-pilula${registrada ? ' rv-pilula-ok' : ''}`}>
      {r ? `${String(r.itensHoje)} hoje` : '— hoje'}
    </span>
  );

  /* ---------------- sucesso (celular/tablet) ---------------- */

  if (registrada && !pc) {
    // D6: a barra é a do cheque (confirmados); o lançado aparece com esse nome
    const m = r ? metaDaSemana(r) : null;
    const pct = m?.pct ?? 0;
    return (
      <>
        <TopoMovel eu={eu} voltar="#/inicio" titulo="Registrar venda" direita={pilula} />
        <div className="rv rv-sucesso">
          <div className="rv-sucesso-cartao">
            <div className="rv-sucesso-topo">
              <span className="rv-sucesso-check" aria-hidden="true">
                <Icone nome="check" tamanho={18} traco={2} />
              </span>
              <div>
                <div className="rv-sucesso-titulo">
                  {registrada.repetido ? 'Venda já estava registrada' : 'Venda registrada'}
                </div>
                <div className="rv-sucesso-sub">
                  {registrada.descricao} · {registrada.quantidade} un ·{' '}
                  {hora(registrada.registradoEm)}
                </div>
              </div>
            </div>
            {mostraValor(ocultos, registrada.premioTotal) && (
              <div className="rv-sucesso-linha rv-sucesso-linha-primeira">
                <span>Premiação desta venda</span>
                <strong className="rv-ouro">{reais(registrada.premioTotal)}</strong>
              </div>
            )}
            <div className="rv-sucesso-linha">
              <span>Sua meta da semana</span>
              <strong>{m ? m.legenda : '—'}</strong>
            </div>
            <div className="rv-barra">
              <div style={{ width: `${String(pct)}%` }} />
            </div>
          </div>
          <button type="button" className="rv-botao-grande" onClick={novaVenda}>
            <Icone nome="plus" tamanho={18} traco={1.8} />
            Registrar outra venda
          </button>
          <button
            type="button"
            className="rv-botao-grande rv-botao-grande-claro"
            onClick={() => void repetir(registrada, pedidoFaixa(registrada, pedidoRef.current))}
          >
            Repetir esta venda
          </button>
          {listaDeHoje}
        </div>
        {sobrepostos}
      </>
    );
  }

  /* ---------------- formulário ---------------- */

  return (
    <>
      <TopoMovel eu={eu} voltar="#/inicio" titulo="Registrar venda" direita={pilula} />
      <div className={`rv rv-form-ativo${produto ? ' rv-com-produto' : ''}`}>
        {bloqueio && (
          <div className="rv-recusado rv-bloqueio-topo" role="alert">
            <span>
              <b>{bloqueio.titulo}</b> {bloqueio.texto}
            </span>
          </div>
        )}
        <div className="rv-titulo-pc">
          <div className="rv-titulo-pc-texto">
            <h1>Registrar venda</h1>
            <span>
              {r
                ? `${String(r.itensHoje)} hoje${mostraValor(ocultos, r.premiacaoHoje) ? ` · ${reais(r.premiacaoHoje)} em premiação` : ''}`
                : ''}
            </span>
          </div>
          <div className="rv-titulo-pc-acoes">
            <button
              type="button"
              onClick={() => ultima && void repetir(ultima)}
              disabled={!ultima || travado || bloqueio !== null}
            >
              Repetir última
            </button>
            {comCamera && (
              <button
                type="button"
                onClick={() => setLeitor(true)}
                disabled={travado || bloqueio !== null}
              >
                Abrir leitor
              </button>
            )}
          </div>
        </div>

        <div className="rv-grade">
          <div className="rv-coluna">
            <section className="rv-form" aria-label="Registrar venda">
              {/* bloqueado: nada do formulário responde — o motivo está no topo */}
              <fieldset
                className={`rv-form-campos${bloqueio ? ' rv-form-travado' : ''}`}
                disabled={bloqueio !== null}
                aria-disabled={bloqueio !== null}
              >
                {seletorDeTipo}
                {campoDeCodigo}
                {cartaoDoProduto}
                {escolhaDeCategoria}
                {precos}
                {comprovacaoPv}
                {comprovacaoParadao}
                {produto && programa === 'vitamina' && (
                  <div className="rv-qtd-obs">
                    {quantidade}
                    {observacao}
                  </div>
                )}
                {produto && programa && programa !== 'vitamina' && (
                  <div className="rv-qtd-obs rv-qtd-obs-pv">
                    {estoqueCurto}
                    {observacao}
                  </div>
                )}
                {programa === 'vitamina' && estoqueCurto}
                {atalhos}
                {rodape}
              </fieldset>
            </section>
          </div>
          <aside className="rv-lado">
            <section className="rv-numeros" aria-label="Seu dia">
              <div className="rv-numeros-linha">
                <div>
                  <div className="rv-numeros-rotulo">ITENS HOJE</div>
                  <div className="rv-numeros-valor">{r ? r.itensHoje : '—'}</div>
                </div>
                {!ocultos && (
                  <div>
                    <div className="rv-numeros-rotulo">PREMIAÇÃO</div>
                    <div className="rv-numeros-valor rv-ouro">
                      {r ? reais(r.premiacaoHoje) : '—'}
                    </div>
                  </div>
                )}
              </div>
              <div className="rv-numeros-meta">
                <div className="rv-numeros-meta-linha">
                  <span>Meta da semana</span>
                  <strong>{r ? metaDaSemana(r).legenda : '—'}</strong>
                </div>
                <div className="rv-barra rv-barra-fina">
                  <div
                    style={{
                      width: `${String(r ? metaDaSemana(r).pct : 0)}%`,
                    }}
                  />
                </div>
              </div>
            </section>
            {listaDeHoje}
          </aside>
        </div>
      </div>
      {sobrepostos}
    </>
  );
}

/** A faixa da última tentativa (para "Repetir esta venda" logo depois do sucesso). */
function pedidoFaixa(v: VendaRegistrada, pedido: PedidoDeVenda | null): string | undefined {
  if (pedido && pedido.productId === v.productId) return pedido.faixa;
  return undefined;
}

function Passo({ feito, n, erro = false }: { feito: boolean; n: number; erro?: boolean }) {
  if (feito)
    return (
      <span className="rv-passo rv-passo-ok" aria-label="feito">
        <Icone nome="check" tamanho={12} traco={2.2} />
      </span>
    );
  return (
    <span className={`rv-passo${erro ? ' rv-passo-erro' : ''}`} aria-label="pendente">
      {n}
    </span>
  );
}

function rotuloGrande(p: ProgramaDeVenda): string {
  return p === 'vitamina' ? 'Vitamina' : p === 'pre_vencido' ? 'Pré-vencido' : 'Paradão';
}

function resumoDaCategoria(p: OpcoesDeVenda, d: ProgramaDeVenda, ocultos: boolean): string {
  if (d === 'vitamina') {
    if (ocultos) return `${String(cardsDaVitamina(p).length)} faixas de preço`;
    return cardsDaVitamina(p)
      .map((c) => `${c.rotulo === 'PREÇO' ? 'Faixa' : `Faixa ${c.rotulo}`} ${reais(c.premio)}`)
      .join(' · ');
  }
  const op = d === 'pre_vencido' ? p.preVencido : p.paradao;
  const base = op?.venderPor
    ? ocultos
      ? `Base ${reais(op.venderPor)}`
      : `Base ${reais(op.premioNaBase)}`
    : ocultos
      ? 'Sem preço base'
      : 'Sem preço base: paga R$ 0,00';
  return d === 'pre_vencido' ? `${base} · exige lote e foto` : `${base} · exige comprovação`;
}

function tempoDesde(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (min < 1) return 'agora';
  if (min < 60) return `há ${String(min)} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${String(h)} h ${String(min % 60)} min`;
  return `há ${String(Math.floor(h / 24))} d`;
}

/* ------------------------------------------------------------------ *
 * Busca pelo nome (tela 08 · 02)
 * ------------------------------------------------------------------ */

function Busca({
  inicial,
  aoFechar,
  aoEscolher,
}: {
  inicial: string;
  aoFechar: () => void;
  aoEscolher: (item: ResultadoDaBusca) => void;
}) {
  const [termo, setTermo] = useState(inicial);
  const [modo, setModo] = useState<'nome' | 'sintoma'>('nome');
  const [itens, setItens] = useState<readonly ResultadoDaBusca[] | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    setErro(false);
    if (termo.trim().length < (modo === 'sintoma' ? 3 : 2)) {
      setItens(null);
      return;
    }
    let vivo = true;
    const t = window.setTimeout(() => {
      if (modo === 'sintoma')
        void buscarPorSintoma(termo).then((r) => {
          if (!vivo) return;
          if (r.ok)
            setItens(
              r.dados.itens.map((i) => ({
                productId: i.productId,
                descricao: i.descricao,
                codigo: i.codigo,
                fabricante: null,
                estoque: null,
                programas: i.programas,
                indicacao: i.indicacao,
              })),
            );
          else setErro(true);
        });
      else
        void buscarProduto(termo).then((r) => {
          if (!vivo) return;
          if (r.ok) setItens(r.dados.itens);
          else setErro(true);
        });
    }, 220);
    return () => {
      vivo = false;
      window.clearTimeout(t);
    };
  }, [termo, modo]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);

  return (
    <div className="rv-busca" role="dialog" aria-label="Buscar produto">
      <div className="rv-busca-topo">
        <button type="button" className="rv-busca-voltar" onClick={aoFechar} aria-label="Voltar">
          <Icone nome="arrow-l" tamanho={20} traco={1.6} />
        </button>
        <label className="rv-busca-campo">
          <Icone nome="search" tamanho={17} traco={1.6} />
          <input
            autoFocus
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder={
              modo === 'sintoma'
                ? 'O que o cliente sente? (dor, alergia, tosse…)'
                : 'Nome, código ou EAN'
            }
            enterKeyHint="search"
            aria-label="Buscar produto"
          />
          {termo && (
            <button type="button" onClick={() => setTermo('')} aria-label="Limpar">
              <Icone nome="x" tamanho={13} traco={2} />
            </button>
          )}
        </label>
      </div>
      <div className="rv-busca-modos" role="radiogroup" aria-label="Buscar por">
        {(['nome', 'sintoma'] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={modo === m}
            className={modo === m ? 'rv-busca-modo rv-busca-modo-sel' : 'rv-busca-modo'}
            onClick={() => setModo(m)}
          >
            {m === 'nome' ? 'Por nome' : 'Por sintoma'}
          </button>
        ))}
      </div>
      <div className="rv-busca-corpo">
        {itens !== null && (
          <div className="rv-busca-conta">
            <span>
              {itens.length} {itens.length === 1 ? 'RESULTADO' : 'RESULTADOS'}
            </span>
            <span>{modo === 'sintoma' ? 'busca por sintoma' : 'busca por nome'}</span>
          </div>
        )}
        {erro && <div className="rv-busca-nota">Não foi possível buscar agora. Tente de novo.</div>}
        {itens?.map((i) => (
          <button
            key={i.productId}
            type="button"
            className="rv-busca-item"
            onClick={() => aoEscolher(i)}
          >
            <span className="rv-busca-item-texto">
              <span className="rv-busca-nome">{i.descricao}</span>
              <span className="rv-busca-meta">
                {[i.codigo, i.fabricante].filter(Boolean).join(' · ')}
              </span>
              {i.indicacao && <span className="rv-busca-indicacao">{i.indicacao}</span>}
              <span className="rv-busca-chips">
                {i.programas.map((p) => (
                  <span key={p} className="rv-chip-sigla">
                    {SIGLA_DO_PROGRAMA[p]}
                  </span>
                ))}
                {i.estoque !== null && (
                  <span
                    className={`rv-chip ${Number(i.estoque) <= 3 ? 'rv-chip-pouco' : 'rv-chip-ok'}`}
                  >
                    {Number(i.estoque) <= 3 ? 'só ' : ''}
                    {unidades(i.estoque)} em estoque
                  </span>
                )}
              </span>
            </span>
            <Icone nome="chev-r" tamanho={16} />
          </button>
        ))}
        {itens !== null && itens.length === 0 && (
          <div className="rv-busca-nota">
            {modo === 'sintoma'
              ? 'Nenhum produto de vitamina, pré-vencido ou paradão para esse sintoma. Na dúvida, chame o farmacêutico.'
              : 'Nenhum produto de vitamina, pré-vencido ou paradão com esse nome.'}
          </div>
        )}
        <div className="rv-busca-nota">
          Produto sem cadastro não pode ser vendido pela premiação. Peça o cadastro a um
          administrador.
        </div>
      </div>
    </div>
  );
}
